import { html, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { DomUtility, SvgUtility } from '~/ui/utils';

import DERFormElement from '~/ui/utils/der-form-element';
import { styles as globalStyles } from '../../scss/main.styles';
import { styles } from './textarea.styles';

export class Textarea extends DERFormElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: String }) label = '';
    @property({ type: String, attribute: 'optional-label' }) optionalLabel = '(Optional)';
    @property({ type: String }) placeholder = '';
    @property({ type: String }) value = '';
    @property({ type: String }) message? = '';
    @property({ type: String }) status?: 'success' | 'error';
    @property({ type: String }) textAreaId: string = DomUtility.uniqueElementId(this);
    @property({ type: String }) name = this.textAreaId;
    @property({ type: Number, attribute: 'min-rows' }) minRows = 2;
    @property({ type: Number, attribute: 'max-rows' }) maxRows = 10;
    @property({ type: Number }) cols = 50;
    @property({ type: Number }) minlength = -1;
    @property({ type: Number }) maxlength = -1;
    @property({ type: Boolean }) disabled = false;
    @property({ type: Boolean }) required = false;
    @property({ type: Boolean }) autoGrow = true;

    @state() private _characterCount = 0;

    @query('textarea') private textareaElement!: HTMLTextAreaElement;

    /**
     * Initializes shadow DOM and character count.
     */
    constructor() {
        super();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
        this._characterCount = this.value.length;
    }

    /**
     * Syncs initial value to form internals and validates after first update.
     */
    override connectedCallback() {
        super.connectedCallback();
        this._characterCount = this.value.length;
        this._internals.setFormValue(this.value);
        this.updateComplete.then(() => this.checkInputValidity());
    }

    /**
     * Reacts to property changes (value / autoGrow) and adjusts height + validity.
     */
    override updated(changedProperties: Map<string, unknown>) {
        super.updated(changedProperties);

        if (!changedProperties.size) {
            return;
        }

        if (changedProperties.has('value') || changedProperties.has('autoGrow')) {
            this.updateComplete.then(() => {
                if (this.autoGrow) {
                    this._adjustHeight();
                }
            });
        }

        this.checkInputValidity();
    }

    /**
     * Runs after first render: applies auto-grow and min-rows CSS var.
     */
    override firstUpdated() {
        if (this.autoGrow && this.textareaElement) {
            this._adjustHeight();
        }

        this.style.setProperty('--min-rows', `${this.minRows}`);
    }

    /**
     * Adjusts the textarea height within min/max row constraints.
     * Ensures auto-grow respects minRows and maxRows.
     */
    private _adjustHeight() {
        const textarea = this.textareaElement;
        if (!textarea) return;

        textarea.style.height = 'auto';

        const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;
        const paddingTop = parseInt(getComputedStyle(textarea).paddingTop) || 0;
        const paddingBottom = parseInt(getComputedStyle(textarea).paddingBottom) || 0;

        const minHeight = this.minRows * lineHeight + paddingTop + paddingBottom;
        const maxHeight = this.maxRows ? this.maxRows * lineHeight + paddingTop + paddingBottom : Infinity;

        const desiredHeight = textarea.scrollHeight;

        if (desiredHeight < minHeight) {
            textarea.style.minHeight = `${minHeight}px`;
            textarea.style.overflowY = 'hidden';
        } else if (this.maxRows && desiredHeight > maxHeight) {
            textarea.style.minHeight = `${maxHeight}px`;
            textarea.style.overflowY = 'auto';
        } else {
            textarea.style.minHeight = `${desiredHeight}px`;
            textarea.style.overflowY = 'hidden';
        }
    }

    /**
     * Handles user input:
     * - Updates component value and form internals
     * - Updates character count and validity
     * - Auto-resizes if enabled
     * - Emits change event with value and characterCount
     * @fires CustomEvent<{
     *   value: string;
     *   characterCount: number;
     * }>('der.change')
     */
    private _handleInput(e: Event) {
        const textarea = e.target as HTMLTextAreaElement;
        this.value = textarea.value;
        this.checkInputValidity();
        this._internals.setFormValue(this.value);
        this._characterCount = textarea.value.length;

        if (this.autoGrow) {
            this._adjustHeight();
        }

        /**
         * Dispatch value + character count after input
         */
        this.dispatchEvent(
            new CustomEvent('der.change', {
                detail: {
                    value: this.value,
                    characterCount: this._characterCount,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Emits focus event when textarea gains focus.
     * @fires CustomEvent<void>('der.focus')
     */
    private _handleFocus() {
        /**
         * Dispatch focus signal
         */
        this.dispatchEvent(
            new CustomEvent('der.focus', {
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Emits blur event with current value when textarea loses focus.
     * @fires CustomEvent<{ value: string }>('der.blur')
     */
    private _handleBlur() {
        /**
         * Dispatch blur with current value
         */
        this.dispatchEvent(
            new CustomEvent('der.blur', {
                detail: { value: this.value },
                bubbles: true,
                composed: true,
            })
        );
    }

    private hasMessage(): boolean {
        return typeof this.message !== 'undefined' && this.message.length > 0;
    }

    private checkInputValidity() {
        if (!this.textareaElement) {
            return;
        }

        this.setDERValidity(
            this.textareaElement?.validity,
            this.textareaElement?.validationMessage,
            this.textareaElement
        );
    }

    override render() {
        return html`
            <div
                class=${classMap({
                    'c-textarea__container': true,
                    disabled: this.disabled,
                    readonly: this.required,
                    error: this.status === 'error',
                    success: this.status === 'success',
                })}
            >
                <div class="c-textarea__label-wrapper">
                    ${this.label
                        ? html`<label for=${this.textAreaId} class="c-textarea__label">${this.label}</label>`
                        : ''}
                    ${!this.required && !this.disabled
                        ? html` <span class="c-textarea__optional-label"> ${this.optionalLabel}</span>`
                        : ''}
                </div>

                <textarea
                    id="${this.textAreaId}"
                    name="${this.name}"
                    placeholder="${ifDefined(this.placeholder)}"
                    rows="${this.minRows}"
                    cols="${this.cols}"
                    .value="${this.value}"
                    ?disabled="${this.disabled}"
                    ?required="${this.required}"
                    minlength="${ifDefined(this.minlength)}"
                    maxlength="${ifDefined(this.maxlength)}"
                    class="${!this.autoGrow ? 'manual-resize' : ''}"
                    @input="${this._handleInput}"
                    @focus="${this._handleFocus}"
                    @blur="${this._handleBlur}"
                ></textarea>

                ${this.hasMessage()
                    ? html`
                          <p class="c-textarea__message">
                              <span class="c-textarea__icon">
                                  <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 40 40"
                                      fill="none"
                                  >
                                      ${SvgUtility.systemAttentionFull}
                                  </svg>
                              </span>
                              ${this.message}
                          </p>
                      `
                    : ''}
            </div>
        `;
    }
}
